import React, { useState, useEffect, useRef, useContext } from 'react';
import { Table, TableHead, TableBody, TableCell, TableContainer, TableRow, Paper, Button, Typography } from '@mui/material';
import ErrorDialog from '../ErrorDialog';
import YesNoDialog from '../YesNoDialog';
import { useParams } from 'react-router-dom';
import { rpc } from '../utils/RpcUtils';
import * as Constants from '../Constants';
import moment from 'moment';
import EventStatus from './EventStatus';
import { Bingo, Card } from './Bingo';
import { AuthContext } from '../AuthContextProvider';
import { Device } from '@twilio/voice-sdk';
import '../App.css';

const BingoEvent = () => {
  const [scheduledEvent, setScheduledEvent] = useState(null);
  const [eventUsers, setEventUsers] = useState([]);
  const [status, setStatus]= useState(scheduledEvent ? EventStatus.getEventStatus(scheduledEvent) : 
    Constants.EVENT_NOT_READY);
  const [lastBall, setLastBall] = useState(null);
  const [ballsChosen, setBallsChosen] = useState('');
  const [conferenceStarted, setConferenceStarted] = useState(false);
  const [potsSpeaker, setPotsSpeaker] = useState('');
  const [potsStatus, setPotsStatus] = useState({});
  const [hasMuted, setHasMuted] = useState({});
  const bingoGame = useRef(null);
  const bingoCards = useRef({});
  const wsClient = useRef(null);
  const twilioDevice = useRef(null);
  const pollingTimer = useRef(null);
  const hasHungUp = useRef({});
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openConfirmCancelDialog, setOpenConfirmCancelDialog] = useState(false);
  const [openConfirmNewGamelDialog, setOpenConfirmNewGameDialog] = useState(false);
  const [openConfirmEndConferencelDialog, setOpenConfirmEndConferenceDialog] = useState(false);
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  // prevent the back button. If the user navigates away and a conference is started, the running
  // conference will be stranded and can't be terminated except through a reload
  window.history.pushState(null, null, window.location.href);
  window.onpopstate = () => window.history.go(1);

  useEffect(() => {
    const fetchEvent = async (id) => {
      const params = {
        _id: id,
      };
      await rpc({
        method: 'GetScheduledEvent',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: setScheduledEvent
      });
    };
    fetchEvent(id);
  }, [id]);

  useEffect(() => {
    const onFetchSuccess = (usersInGroup) => {
      setEventUsers((prevUsers) => {
        const allUsers = prevUsers.concat(usersInGroup);
        const uniqueIds = new Set();
        const result = allUsers.filter((user) => {
          const isDuplicate = uniqueIds.has(user._id);
          uniqueIds.add(user._id);
          return !isDuplicate;
        });
        return result;
      });
    };
  
    const fetchUsersInGroup = async (group_id) => {
      const params = {
        _eventGroups: group_id,
      };
      rpc({
        method: 'GetUsers',
        params,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: onFetchSuccess
      });
    };
    if (scheduledEvent !== null) {
      setStatus(EventStatus.getEventStatus(scheduledEvent));
      scheduledEvent._eventGroups.forEach(group => {
        fetchUsersInGroup(group);
      });
    }
  }, [scheduledEvent]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (scheduledEvent !== null) {
        setStatus(EventStatus.getEventStatus(scheduledEvent));
      }
    }, 5000); // Refresh every 5 seconds    
    return () => {
      // Clean up the timer when the component unmounts
      clearInterval(timer);
    };
  }, [scheduledEvent]);

  /****************************************************************************/
  
  const getPhoneNumber = (user) => {
    return user.phone.replace(/\D/g, '');
  };

  const getPhoneNumbersToDial = () => {
    const pots = [];
    eventUsers.forEach(user => {
      if (!user.useTwilio) {
        const muted = hasMuted[user.email] ? true : false;
        pots.push(`${getPhoneNumber(user)}:${muted}:${user.email}`);
      }
    });
    return pots.join(', ');
  };

  const endConference = () => {
    stopPollingForUserStatus();
    setOpenConfirmEndConferenceDialog(false);
    setConferenceStarted(false);
    setPotsSpeaker('');
    setPotsStatus({});
    if (twilioDevice.current !== null) {
      twilioDevice.current.disconnectAll();
      twilioDevice.current.destroy();
      twilioDevice.current = null;
    }
    hangupAll();
  };

  const startConference = async () => {
    const response = await fetch('/itvs-site/capability-token', {
      headers : {
        'Accept': 'application/json'
      },
    });
    const token = await response.json();
    if (token.error) {
      setErrorMessage(token.error.message);
      setOpenErrorDialog(true);
    } else {
      try {
        setConferenceStarted(true);
        twilioDevice.current = new Device(token.token, {logLevel: 'warn'});
        twilioDevice.current.on('error', function (error) {
          console.error('Device Error: ' + error.message);
        });
        twilioDevice.current.on('registered', function () {
          console.log('Device.connect registered');
        });
        const params = {
          To: getPhoneNumbersToDial(),
          Initialize: true,
          site: user._site._id,
          eventid: scheduledEvent._id,
        };
        await twilioDevice.current.connect({params});
        startPollingForUserStatus();
        setTimeout(() => {
          setEventStatus({status: Constants.EVENT_TELECON_STARTED});
        }, 1000);
      } catch (err) {
        console.error("Unexpected Error in startConference", err);
      }
    }
  };

  const setEventStatus = async ({status, ball}) => {
    EventStatus.setEventStatus(scheduledEvent, status);
    setStatus(status);
    const params = {
      _id: scheduledEvent._id,
      status,
    };
    if (status === Constants.EVENT_BINGO_GAME) {
      params.bingoGame = JSON.stringify(bingoCards.current);
    } else if (status === Constants.EVENT_BINGO_BALL) {
      params.bingoBall = ball.toString();
    }
    rpc({
      method: 'UpdateScheduledEvent',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess: () => {console.log("UpdateScheduleEvent success");}
    });
  };

  /** Bingo Game 
   ***************************************************************************/

  const onNewGame = async () => {
    if (lastBall !== null) {
      setOpenConfirmNewGameDialog(true);
    } else {
      newGame();
    }
  };

  const newGame = async () => {
    bingoGame.current = new Bingo();
    bingoCards.current = {};
    setLastBall(null);
    setBallsChosen('');
    eventUsers.forEach(user => {
      bingoCards.current[user.email] = new Card(user.email);
    });
    setEventStatus({status: Constants.EVENT_BINGO_GAME});
    setOpenConfirmNewGameDialog(false);
  };

  const pickBall = async () => {
    const currBall = bingoGame.current.chooseBall();
    setLastBall(currBall);
    setBallsChosen((prevBallsChosen) => {
      return prevBallsChosen + currBall.toString() + ', ';
    });
    Object.keys(bingoCards.current).forEach(email => {
      bingoCards.current[email].ballChosen(currBall);
    });
    setEventStatus({status: Constants.EVENT_BINGO_BALL, ball: currBall});
  };

  const getBingoCardString = (user) => {
    const card = bingoCards.current[user.email];
    if (card) {
      return(
        <div>
          &nbsp;B&nbsp;&nbsp;&nbsp;&nbsp;I&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;N&nbsp;&nbsp;&nbsp;&nbsp;G&nbsp;&nbsp;&nbsp;O<br/>
          {card.getRow(0)}<br/>
          {card.getRow(1)}<br/>
          {card.getRow(2)}<br/>
          {card.getRow(3)}<br/>
          {card.getRow(4)}<br/>
        </div>
      );
    } else {
      return null;
    }
  };

  /** websocket polling for telecon user status
   ***************************************************************************/

  const getPotsStatus = (user) => {
    const phone = getPhoneNumber(user);
    const email = user.email;
    if (potsStatus[phone]) {
      return potsStatus[phone];
    }
    if (potsStatus[email]) {
      return potsStatus[email];
    }
    return 'unknown';
  };

  const isSpeaking = (user) => {
    return potsSpeaker === getPhoneNumber(user);
  };

  const startPollingForUserStatus = () => {
    wsClient.current = new WebSocket(Constants.WEBSOCKET_URL);
    wsClient.current.onopen = () => {
      console.log('WebSocket Client Connected');
    };
    wsClient.current.onmessage = (message) => {
      const m = JSON.parse(message.data);
      //console.log('message', m);
      if (m.type === 'getPotsSpeaker') {
        setPotsSpeaker(m.speaker);
        setPotsStatus(m.status);
      }
    };
    wsClient.current.onerror = (error) => {
      console.error('WebSocket error', error);
    };
    wsClient.current.onclose = () => {
      console.log('WebSocket Client Closed');
      if (pollingTimer.current) {
        clearInterval(pollingTimer.current);
      }
    };

    pollingTimer.current = setInterval(() => {
      wsClient.current.send(JSON.stringify({
        type: "getPotsSpeaker",
        site: user._site._id,
        eventid: scheduledEvent._id
      }));
    }, 1000);
  };

  const stopPollingForUserStatus = () => {
    if (pollingTimer.current) {
      clearInterval(pollingTimer.current);
    }
    if (wsClient.current) { 
      wsClient.current.close();
    }
  };

  /** POTS hangup and redial
   ***************************************************************************/

  const redial = async (eventUser) => {
    try {
      const phone = getPhoneNumber(eventUser);
      const muted = hasMuted[eventUser.email] ? true : false;
      hasHungUp.current[phone] = false;
      const to = `${phone}:${muted}:${eventUser.email}`;
      const params = new URLSearchParams();
      params.append('site', user._site._id);
      params.append('eventid', scheduledEvent._id);
      params.append('To', to);
      const response = await fetch('/itvs-site/conference2', {
        method: "post",
        headers : {
          'Accept': 'application/json'
        },
        body: params,
      });
      if (response.status !== 200) {
        throw new Error(`Unexpected response status ${response.status}`);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(`failed to redial ${err}`);
      setOpenErrorDialog(true);
    }
  };

  const hangup = async (eventUser) => {
    try {
      const phone = getPhoneNumber(eventUser);
      hasHungUp.current[phone] = true;
      const params = new URLSearchParams({
        site: user._site._id,
        number: phone,
        email: eventUser.email,
        eventid: scheduledEvent._id
      });
      const response = await fetch('/itvs-site/delete?' + params, {
        headers : {
          'Accept': 'application/json'
        }
      });
      const json = await response.json();
      console.log ('hangup response', json);
    } catch (err) {
      console.error(err);
      setErrorMessage(`hangup ${err}`);
      setOpenErrorDialog(true);
    }
  };

  const hangupAll = () => {
    eventUsers.forEach(user => {
      if (!user.useTwilio) {
        hangup(user);
      }
    });
  };

  /** Mute
   ***************************************************************************/

  const muteOrUnmute = async (status, eventUser) => {
    try {
      const muted = status === Constants.USER_MUTED ? true : false;
      const value = {[eventUser.email]: muted};
      setHasMuted(prev => ({
        ...prev,
        ...value
      }));
      const updatePrams = {
        _id: eventUser._id,
        status
      };
      await rpc({
        method: 'UpdateUser',
        params: updatePrams,
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: (user) => {console.log("UpdateUser success", user);}
      });

      if (!eventUser.useTwilio) {
        const params = new URLSearchParams({
          site: user._site._id,
          number: getPhoneNumber(eventUser),
          muted,
          email: eventUser.email,
          eventid: scheduledEvent._id
        });
        const response = await fetch('/itvs-site/mute?' + params, {
          headers : {
            'Accept': 'application/json'
          }
        });
        const json = await response.json();
        console.log ('mute response', json);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(`muteOrUnmute ${err}`);
      setOpenErrorDialog(true);
    }
  };

  const muteAll = () => {
    eventUsers.forEach(user => {
      muteOrUnmute(Constants.USER_MUTED, user);
    });
  };

  const unMuteAll = () => {
    eventUsers.forEach(user => {
      muteOrUnmute(Constants.USER_UNMUTED, user);
    });
  };

  /****************************************************************************/

  const onCancelYes = async () => {
    if (conferenceStarted) {
      endConference();
    }
    setEventStatus({status: Constants.EVENT_CANCELLED});
    setOpenConfirmCancelDialog(false);
  };

  const getTitle = () => {
    if (scheduledEvent === null) {
      return '';
    }
    const shownOn = moment(scheduledEvent.shownOn).format(Constants.TIME_FORMAT);
    const statusText= Constants.EVENT_STATUS_TEXT[status];
    return`${scheduledEvent._event.displayName} ${shownOn} ${statusText}`;
  };

  const canStartNewGame = () => {
    return status === Constants.EVENT_STOPPED || status === Constants.EVENT_BINGO_GAME || 
      status === Constants.EVENT_BINGO_BALL || status === Constants.EVENT_TELECON_STARTED;
  };

  const canPickBall = () => {
    return status === Constants.EVENT_BINGO_GAME || status === Constants.EVENT_BINGO_BALL || 
      status === Constants.EVENT_TELECON_STARTED;
  };
 
  const canCancel = () => {
    return status === Constants.EVENT_STOPPED || status === Constants.EVENT_BINGO_GAME || 
      status === Constants.EVENT_BINGO_BALL ||  status === Constants.EVENT_TELECON_STARTED;

  };

  const canMute = () => {
    return status !== Constants.EVENT_CANCELLED && status !== Constants.EVENT_NOT_READY &&
      status !== Constants.EVENT_ABOUT_TO_START;
  };
  
  const canStartConference = () => { 
    return !conferenceStarted && (status === Constants.EVENT_STOPPED || 
      status === Constants.EVENT_BINGO_GAME || status === Constants.EVENT_TELECON_STARTED);
  };

  const getPhoneButtonCells = (user) => {
    if (!user.useTwilio) {
      const phone = getPhoneNumber(user);
      if (hasHungUp.current[phone] === true) {
        return (
          <>
            <TableCell>
              <Button className="UserButton" variant="contained" color="primary"
                disabled = {!conferenceStarted}
                onClick={() => {redial(user);}}>
              ReDial
              </Button>
            </TableCell>
          </>
        );
      } else {
        return (
          <>
            <TableCell>
              <Button className="UserButton" variant="contained" color="primary"
                disabled = {!conferenceStarted}
                onClick={() => {hangup(user);}}>
              Hangup
              </Button>
            </TableCell>
          </>
        );
      }
    }
    return (
      <>
        <TableCell></TableCell>
      </>
    );
  };

  const getMuteCells = (user) => {
    if (hasMuted[user.email]) {
      return (
        <>
          <TableCell>
            <Button className="UserButton" variant="contained" color="primary"
              disabled = {!conferenceStarted}
              onClick={() => {muteOrUnmute(Constants.USER_UNMUTED, user);}}>
            UnMute
            </Button>
          </TableCell>
        </>
      );
    } else {
      return (
        <>
          <TableCell>
            <Button className="UserButton" variant="contained" color="primary"
              disabled = {!conferenceStarted}
              onClick={() => {muteOrUnmute(Constants.USER_MUTED, user);}}>
            Mute
            </Button>
          </TableCell>
        </>
      );
    }
  };


  return (
    <div className="BingoEventDetails">
      <Typography variant="h6" gutterBottom>
        {getTitle()}
      </Typography>
      <Typography variant="h8">
        Ball: {lastBall !== null ? lastBall.toString() : ''}
      </Typography>
      <Typography variant="h8">
        Balls Chosen: {ballsChosen}
      </Typography>
      <div className="HorizontalContainer">
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canStartNewGame()}
          onClick={onNewGame}>
          New Game
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canPickBall()}
          onClick={pickBall}>
          Pick Ball
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canCancel()}
          onClick={() => {setOpenConfirmCancelDialog(true);}}>
          Cancel
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canStartConference()}
          onClick={startConference}>
          Start Telecon
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!conferenceStarted}
          onClick={() => {setOpenConfirmEndConferenceDialog(true);}}>
          End Telecon
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canMute()}
          onClick={() => {muteAll();}}>
          Mute All
        </Button>
        <Button className="Button" variant="contained" color="primary" 
          disabled={!canMute()}
          onClick={() => {unMuteAll();}}>
          UnMute All
        </Button>
      </div>
      <TableContainer className="TableContainer" component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%' }}>Name</TableCell>
              <TableCell sx={{ width: '10%' }}>Phone</TableCell>
              <TableCell sx={{ width: '10%' }}>Status</TableCell>
              <TableCell sx={{ width: '20%' }}>Card</TableCell>
              <TableCell sx={{ width: '10%' }}>Dial</TableCell>
              <TableCell sx={{ width: '10%' }}>Mute</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {eventUsers.map((user, index) => (
              <TableRow key={index}>
                <TableCell>{`${user.first} ${user.last}`}</TableCell>
                <TableCell style={isSpeaking(user) ? {color: 'red'} : {}}>{getPhoneNumber(user)}</TableCell>
                <TableCell>{getPotsStatus(user)}</TableCell>
                <TableCell>{getBingoCardString(user)}</TableCell>
                {getPhoneButtonCells(user)}
                {getMuteCells(user)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ErrorDialog open={openErrorDialog} 
        onErrorDialogClose={() => {setOpenErrorDialog(false);}}
        errorMessage={errorMessage} />
      <YesNoDialog open={openConfirmCancelDialog} 
        onErrorDialogClose={() => {setOpenConfirmCancelDialog(false);}}
        onYes={onCancelYes} 
        title="Cancel Event"
        message="Are you sure you want to cancel this event?"
      />
      <YesNoDialog open={openConfirmNewGamelDialog} 
        onErrorDialogClose={() => {setOpenConfirmNewGameDialog(false);}}
        onYes={newGame} 
        title="New Game"
        message="Are you sure you want to start a new game?"
      />
      <YesNoDialog open={openConfirmEndConferencelDialog} 
        onErrorDialogClose={() => {setOpenConfirmEndConferenceDialog(false);}}
        onYes={endConference} 
        title="End Teleconference"
        message="Are you sure you want to end the conference?"
      />
    </div>
  );
};

export default BingoEvent;
